import { useTranslation } from "react-i18next";
import { Box, Container, Flex, Text } from "@chakra-ui/react";
import { useState, useRef } from "react";

const GREEN = '#7A1F35';

const FAQItem = ({ question, answer }) => {
    const [open, setOpen] = useState(false);
    const contentRef = useRef(null);
    const contentHeight = contentRef.current?.scrollHeight ?? 0;

    return (
        <Box
            border={'1px solid'}
            borderColor={'gray.200'}
            borderRadius={'8px'}
            overflow={'hidden'}
        >
            <Flex
                px={'20px'}
                py={'16px'}
                justifyContent={'space-between'}
                alignItems={'center'}
                cursor={'pointer'}
                onClick={() => setOpen(!open)}
                transition={'background 0.2s'}
                bg={open ? GREEN : undefined}
                _hover={{ bg: GREEN }}
                role={'group'}
            >
                <Text
                    fontWeight={600}
                    fontSize={'15px'}
                    pr={'12px'}
                    transition={'color 0.2s'}
                    _groupHover={{ color: 'white' }}
                    color={open ? 'white' : '#1E1517'}
                >
                    {question}
                </Text>
                <Text
                    fontSize={'20px'}
                    fontWeight={300}
                    lineHeight={1}
                    flexShrink={0}
                    transition={'color 0.2s'}
                    _groupHover={{ color: 'white' }}
                    color={open ? 'white' : 'gray.500'}
                >
                    {open ? '−' : '+'}
                </Text>
            </Flex>
            <div
                style={{
                    maxHeight: open ? `${contentHeight}px` : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease',
                }}
            >
                <Box ref={contentRef} px={'20px'} pt={'12px'} pb={'18px'}>
                    <Text fontWeight={400} fontSize={'14px'} lineHeight={'22px'} color={'#3B3033'}>
                        {answer}
                    </Text>
                </Box>
            </div>
        </Box>
    );
};

const FAQ = () => {
    const { t } = useTranslation("faq");

    const sections = [
        {
            title: 's1.title',
            items: [
                { q: 's1.q1', a: 's1.a1' },
                { q: 's1.q2', a: 's1.a2' },
                { q: 's1.q3', a: 's1.a3' },
                { q: 's1.q4', a: 's1.a4' },
                { q: 's1.q5', a: 's1.a5' },
            ],
        },
        {
            title: 's2.title',
            items: [
                { q: 's2.q1', a: 's2.a1' },
                { q: 's2.q2', a: 's2.a2' },
                { q: 's2.q3', a: 's2.a3' },
                { q: 's2.q4', a: 's2.a4' },
                { q: 's2.q5', a: 's2.a5' },
            ],
        },
        {
            title: 's3.title',
            items: [
                { q: 's3.q1', a: 's3.a1' },
                { q: 's3.q2', a: 's3.a2' },
                { q: 's3.q3', a: 's3.a3' },
                { q: 's3.q4', a: 's3.a4' },
                { q: 's3.q5', a: 's3.a5' },
            ],
        },
        {
            title: 's4.title',
            items: [
                { q: 's4.q1', a: 's4.a1' },
                { q: 's4.q2', a: 's4.a2' },
                { q: 's4.q3', a: 's4.a3' },
                { q: 's4.q4', a: 's4.a4' },
            ],
        },
        {
            title: 's5.title',
            items: [
                { q: 's5.q1', a: 's5.a1' },
                { q: 's5.q2', a: 's5.a2' },
                { q: 's5.q3', a: 's5.a3' },
                { q: 's5.q4', a: 's5.a4' },
            ],
        },
        {
            title: 's6.title',
            items: [
                { q: 's6.q1', a: 's6.a1' },
                { q: 's6.q2', a: 's6.a2' },
                { q: 's6.q3', a: 's6.a3' },
                { q: 's6.q4', a: 's6.a4' },
            ],
        },
        {
            title: 's7.title',
            items: [
                { q: 's7.q1', a: 's7.a1' },
                { q: 's7.q2', a: 's7.a2' },
                { q: 's7.q3', a: 's7.a3' },
            ],
        },
        {
            title: 's8.title',
            items: [
                { q: 's8.q1', a: 's8.a1' },
                { q: 's8.q2', a: 's8.a2' },
                { q: 's8.q3', a: 's8.a3' },
            ],
        },
    ];

    return (
        <>
            <Box w={'full'} fontFamily="body">
                <Container maxW={'7xl'} py={'64px'}>
                    <Flex flexDirection={'column'} gap={'8px'}>

                        {/* Nagłówek strony */}
                        <Text
                            fontWeight={700}
                            fontSize={'26px'}
                            lineHeight={'38px'}
                            color={'#1E1517'}
                            textAlign={'center'}
                            mb={'4px'}
                        >
                            {t('page_title')}
                        </Text>

                        {/* Intro */}
                        <Text
                            fontWeight={400}
                            fontSize={'15px'}
                            lineHeight={'26px'}
                            color={'#3B3033'}
                            textAlign={'center'}
                            maxW={'720px'}
                            mx={'auto'}
                            mb={'32px'}
                        >
                            {t('intro')}
                        </Text>

                        {/* Separator */}
                        <Flex w={'full'} h={'1px'} bg={'#ECE4E4'} mb={'32px'} />

                        {/* Sekcje FAQ */}
                        <Flex gap={'40px'} flexDirection={'column'} w={'full'}>
                            {sections.map((section) => (
                                <Flex key={section.title} flexDirection={'column'} gap={'12px'}>
                                    <Text
                                        fontWeight={700}
                                        fontSize={'18px'}
                                        lineHeight={'28px'}
                                        color={'#1E1517'}
                                        mt={'8px'}
                                        mb={'4px'}
                                    >
                                        {t(section.title)}
                                    </Text>
                                    {section.items.map((item) => (
                                        <FAQItem
                                            key={item.q}
                                            question={t(item.q)}
                                            answer={t(item.a)}
                                        />
                                    ))}
                                </Flex>
                            ))}
                        </Flex>

                    </Flex>
                </Container>
            </Box>
        </>
    );
};

export default FAQ;
